import { useState } from 'react';

import type { TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Alert, Skeleton, Stack } from '@mui/material';
import type { GridRowParams } from '@mui/x-data-grid-pro';
import { DataGridErrorState } from 'ml-react-component-library';
import { useNavigate, useParams } from 'react-router';
import { useSetAtom } from 'jotai';

import { GET_RELATED_CLIENTS_WITH_DOCUMENT_COUNTS } from '@/apps/engagement-letter-database/graphql/queries';
import { engagementLetterDatabaseRoutes } from '@/apps/engagement-letter-database/routes';
import type { RelatedClientRecord, RelatedClientRow } from '@/apps/engagement-letter-database/data/types';
import { useBreadcrumb } from '@/shared/components/Breadcrumbs';
import { useCurrentUserMpId } from '@/shared/hooks';
import { openModalAtom } from '@/shared/state/atoms';

import RelatedClientInfoPane from './components/RelatedClientInfoPane';
import HeaderPane from './components/HeaderPane';
import RelatedClientsModal from './components/Modal';
import SearchResults from './components/SearchResults';
import CommentModal from './components/CommentModal';

type MemberClient = RelatedClientRecord['Clients'][number];

const parseClientDate = (value: number | null | undefined): Date | null => {
  if (value == null || value < 0) return null;

  const year = Math.floor(value / 10000);
  const month = Math.floor((value % 10000) / 100);
  const day = value % 100;
  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};

const relatedClientQuery = GET_RELATED_CLIENTS_WITH_DOCUMENT_COUNTS as TypedDocumentNode<
  { mlfsl_related_client: RelatedClientRecord[] },
  { limit: number; where: { RelatedClientNumber: { _eq: string } } }
>;

const RelatedClientsDashboard = () => {
  const currentUserMpId = useCurrentUserMpId();
  const openModal = useSetAtom(openModalAtom);
  const [selectedClients, setSelectedClients] = useState<MemberClient[]>([]);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const { relatedClientId } = useParams<{ relatedClientId: string }>();
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useQuery(relatedClientQuery, {
    variables: { limit: 1, where: { RelatedClientNumber: { _eq: relatedClientId ?? '' } } },
    skip: !relatedClientId,
  });
  const record = data?.mlfsl_related_client[0];

  useBreadcrumb({ label: record?.RelatedClientName ?? '', hideIntermediateSegments: true });

  if (loading) return <Skeleton variant="rectangular" height={300} />;
  if (error) {
    return <DataGridErrorState message={error.message} onRetry={() => void refetch()} />;
  }
  if (!record) return <Alert severity="info">Related client not found.</Alert>;

  const handleRowClick = (params: GridRowParams<RelatedClientRow>) => {
    void navigate(engagementLetterDatabaseRoutes.client.path.replace(':clientId', params.row.ClientNumber));
  };

  const handleAddDocument = (clients: MemberClient[]) => {
    setSelectedClients(clients);
    openModal({ mode: 'add', data: null });
  };

  const handleAddComment = (clients: MemberClient[]) => {
    setSelectedClients(clients);
    setIsCommentModalOpen(true);
  };

  const clients = record.Clients.map((client) => ({
    ...client,
    responsibleAttorney: client.ResponsibleAttorney?.NamePreferredFull ?? '',
    practice: client.ResponsibleAttorney?.PrimaryPracticeGroup?.SubSubPracticeGroupName ?? '',
    openDate: parseClientDate(client.DateClientOpen),
    closeDate: parseClientDate(client.DateClientClosed),
    isExecuted: (client.cmap_clm_engagement_record?.engagement_imanage_documents_aggregate?.aggregate?.count ?? 0) > 0,
    documentCount: client.cmap_clm_engagement_record?.engagement_imanage_documents_aggregate?.aggregate?.count ?? 0,
  }));

  return (
    <Stack spacing={2}>
      <HeaderPane record={record} />
      <RelatedClientInfoPane record={record} />
      <SearchResults
        rows={clients}
        onRowClick={handleRowClick}
        onAddDocument={handleAddDocument}
        onAddComment={handleAddComment}
      />
      <RelatedClientsModal
        currentUserMpId={currentUserMpId}
        selectedClients={selectedClients}
        onDocumentAdded={() => void refetch()}
      />
      {isCommentModalOpen && (
        <CommentModal
          currentUserMpId={currentUserMpId}
          selectedClients={selectedClients}
          onClose={() => setIsCommentModalOpen(false)}
        />
      )}
    </Stack>
  );
};

export default RelatedClientsDashboard;
